/**
 * React-Query hooks for the saved-designs API.
 *
 * Shared across all modules — call sites pass the `module` filter to
 * scope the list. Cache key includes module + search + page so two
 * different modules don't step on each other's data.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  DesignCreatePayload,
  DesignFull,
  DesignListResponse,
  DesignModule,
  DesignUpdatePayload,
} from './types';

interface ListParams {
  module?: DesignModule;
  q?: string;
  page?: number;
  per_page?: number;
}

/** List the user's designs, optionally filtered by module / search / page. */
export function useDesignsList(params: ListParams) {
  return useQuery<DesignListResponse>({
    queryKey: ['designs', 'list', params],
    queryFn: async () =>
      (
        await api.get<DesignListResponse>('/api/designs', {
          params: {
            module: params.module,
            q: params.q || undefined,
            page: params.page ?? 1,
            per_page: params.per_page ?? 20,
          },
        })
      ).data,
  });
}

/** Load one saved design (full payload). */
export function useDesign(id: number | undefined | null) {
  return useQuery<DesignFull>({
    enabled: !!id,
    queryKey: ['designs', 'one', id],
    queryFn: async () => (await api.get<DesignFull>(`/api/designs/${id}`)).data,
  });
}

export function useCreateDesign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DesignCreatePayload) =>
      (await api.post<DesignFull>('/api/designs', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs', 'list'] });
    },
  });
}

export function useUpdateDesign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: DesignUpdatePayload }) =>
      (await api.patch<DesignFull>(`/api/designs/${id}`, data)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['designs', 'list'] });
      qc.invalidateQueries({ queryKey: ['designs', 'one', data.id] });
    },
  });
}

export function useDeleteDesign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`/api/designs/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs', 'list'] });
    },
  });
}
